import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { useLanguage } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Building2, ArrowRight, Loader2, AlertCircle, Check } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { register } = useAuth();
  const { t } = useLanguage();
  const a = t.auth;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [plan, setPlan] = useState<'starter' | 'founder' | 'scale'>(
    (params.get('plan') as 'starter' | 'founder' | 'scale') || 'starter'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const planOptions = [
    { key: 'starter' as const, label: t.plans.starter, price: t.landing.plans.starter.price, desc: t.landing.plans.starter.description },
    { key: 'founder' as const, label: t.plans.founder, price: `$29${t.landing.plans.founder.period}`, desc: t.landing.plans.founder.description },
    { key: 'scale' as const, label: t.plans.scale, price: `$79${t.landing.plans.scale.period}`, desc: t.landing.plans.scale.description },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError(a.errors.weakPassword);
      return;
    }
    if (password !== confirm) {
      setError(a.errors.passwordMismatch);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await register(email, password, plan);
      navigate('/login?registered=true');
    } catch (err) {
      setError(err instanceof Error ? err.message : a.errors.invalidCredentials);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <nav className="bg-white border-b border-cloud">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-500 flex items-center justify-center">
              <Building2 size={18} className="text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">{t.brand}</span>
          </Link>
          <LanguageSwitcher />
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-cloud p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-black mb-1">{a.createAccount}</h1>
          <p className="text-sm text-slate mb-6">{t.landing.ctaPrimary}</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-700 text-sm flex items-start gap-2">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-steel mb-1">{a.email}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 min-h-[44px] border border-cloud bg-white text-base focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                placeholder="founder@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-steel mb-1">{a.password}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 min-h-[44px] border border-cloud bg-white text-base focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                placeholder="••••••••"
              />
              <p className="text-xs text-ash mt-1">{a.passwordHint}.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-steel mb-1">{a.confirmPassword}</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="w-full px-3 py-2 min-h-[44px] border border-cloud bg-white text-base focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                placeholder="••••••••"
              />
            </div>

            <div className="space-y-2">
              <p className="block text-sm font-medium text-steel">{a.plan}</p>
              {planOptions.map((p) => (
                <label
                  key={p.key}
                  className={`flex items-start gap-3 p-3 border min-h-[44px] cursor-pointer transition-colors ${
                    plan === p.key ? 'border-red-500 bg-red-50' : 'border-cloud hover:bg-mist'
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={p.key}
                    checked={plan === p.key}
                    onChange={() => setPlan(p.key)}
                    className="mt-1 text-red-500 focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm text-black">{p.label}</span>
                      <span className="text-xs font-medium text-slate">{p.price}</span>
                    </div>
                    <p className="text-xs text-ash">{p.desc}</p>
                  </div>
                  {plan === p.key && <Check size={16} className="text-red-500 shrink-0" />}
                </label>
              ))}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full min-h-[44px] py-3 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>{a.createAccount} <ArrowRight size={18} /></>}
            </button>
          </form>

          <p className="mt-6 text-sm text-center text-slate">
            {a.alreadyAccount}{' '}
            <Link to="/login" className="text-red-600 hover:text-red-700 font-medium">
              {a.signIn}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
